// Customer Detail / Requirements page.
// Loaded at /customers/:id. Shows the Rentalman customer header, manages
// ship-to sites, and edits the DISA / Training / Safety Council / Per Diem
// / Rate / Badging requirements at the customer level (apply to all sites)
// or per-site (override).
//
// Reads: any authenticated user (incl. ops — dispatch pulls badging docs).
// Writes: admin / leadership / supervisor only. Ops can also run the
// badging form filler (generate + email packets), which writes nothing
// to the requirements themselves.

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { styles, colors, fmtDateTime, fmtMoney } from '../components/UI';
import { MappingModal, FillWizard, DOC_TYPES } from '../components/BadgingFiller';
import { codesToCheck, rowWarnings } from '../utils/reqCodes';

const canEdit = (user) => isAdminLike(user) || user.role === 'supervisor';

export default function CustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const editAllowed = canEdit(user);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('customer'); // 'customer' | 'site-<id>'

  const reload = () => api.getCustomerRequirements(id).then(setData).catch(e => setError(e.message));
  useEffect(() => { reload(); }, [id]);

  if (error) return <div style={{ ...styles.errorBox, margin: 16 }}>{error}</div>;
  if (!data) return <div style={{ padding: 24, color: '#6B7280' }}>Loading customer detail…</div>;

  const { customer, sites, customer_requirements, site_requirements, baseline_requirements } = data;
  const activeSites = sites.filter(s => s.is_active);
  const siteReqByid = new Map(site_requirements.map(r => [r.site_id, r]));

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Link to="/customers" style={{ fontSize: 12, color: colors.orange, textDecoration: 'none' }}>← Back to Customers</Link>
      </div>

      {/* Header: Rentalman master fields (read-only) */}
      <div style={{ ...styles.card, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h1 style={{ ...styles.h1, margin: 0 }}>{customer.name}</h1>
            <div style={{ color: '#6B7280', fontSize: 12, marginTop: 4 }}>
              Acct #{customer.account_no}
              {customer.status ? ` · ${customer.status}` : ''}
              {customer.phone ? ` · ${customer.phone}` : ''}
              {customer.email ? ` · ${customer.email}` : ''}
            </div>
            {(customer.address1 || customer.state) && (
              <div style={{ color: '#6B7280', fontSize: 12, marginTop: 2 }}>
                {[customer.address1, customer.address2, customer.state, customer.zip_code].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
          <CascadeToggle
            current={customer.reqs_cascade_to_sites}
            customerId={customer.id}
            disabled={!editAllowed}
            onSaved={(next) => setData(d => ({ ...d, customer: { ...d.customer, reqs_cascade_to_sites: next } }))}
            onError={setError}
          />
        </div>
      </div>

      {/* Sites manager */}
      <SitesSection
        customerId={customer.id}
        sites={sites}
        editAllowed={editAllowed}
        onChange={reload}
        onError={setError}
      />

      {/* Tab strip: Customer-level + one per active site */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap', borderBottom: '1px solid #E5E7EB' }}>
        <TabBtn active={tab === 'customer'} onClick={() => setTab('customer')}>
          📋 Customer Level
          {customer.reqs_cascade_to_sites && <span style={{ marginLeft: 4, fontSize: 10, color: '#065F46' }}>(cascades)</span>}
        </TabBtn>
        {activeSites.map(s => {
          const hasOverride = siteReqByid.has(s.id);
          return (
            <TabBtn key={s.id} active={tab === `site-${s.id}`} onClick={() => setTab(`site-${s.id}`)}>
              🏢 {s.name}
              {hasOverride
                ? <span style={{ marginLeft: 4, fontSize: 10, color: '#92400E' }}>(override)</span>
                : (customer.reqs_cascade_to_sites && <span style={{ marginLeft: 4, fontSize: 10, color: '#6B7280' }}>(inherits)</span>)}
            </TabBtn>
          );
        })}
      </div>

      {tab === 'customer' && (
        <RequirementsForm
          key="customer-level"
          scope="customer"
          customerId={customer.id}
          existing={customer_requirements}
          baseline={baseline_requirements}
          editAllowed={editAllowed}
          headerNote={customer.reqs_cascade_to_sites
            ? 'These requirements apply to every site (and any future site) unless that site sets its own override.'
            : 'Cascade is OFF — these only apply when you explicitly inherit them on a site. (Toggle cascade above.)'}
          onSaved={reload}
          onError={setError}
        />
      )}

      {tab.startsWith('site-') && (() => {
        const sid = parseInt(tab.replace('site-', ''), 10);
        const site = sites.find(s => s.id === sid);
        if (!site) return <div style={{ padding: 24, color: '#9CA3AF' }}>Site not found.</div>;
        const existing = siteReqByid.get(sid) || null;
        return (
          <RequirementsForm
            key={`site-${sid}`}
            scope="site"
            customerId={customer.id}
            siteId={sid}
            siteName={site.name}
            existing={existing}
            baseline={baseline_requirements}
            editAllowed={editAllowed}
            inheritedFrom={!existing && customer.reqs_cascade_to_sites ? customer_requirements : null}
            headerNote={existing
              ? `${site.name} has its own override. Edits here only affect this site.`
              : (customer.reqs_cascade_to_sites && customer_requirements
                  ? `${site.name} currently inherits from the customer-level requirements. Save anything below to create an override for this site.`
                  : `No requirements defined for ${site.name} yet.`)}
            onSaved={reload}
            onError={setError}
            onDeleteOverride={async () => {
              if (!existing) return;
              if (!confirm(`Remove this site's override? ${site.name} will fall back to the customer-level requirements.`)) return;
              try { await api.deleteSiteLevelRequirements(sid); reload(); }
              catch (e) { setError(e.message); }
            }}
          />
        );
      })()}
    </>
  );
}

// ---------------------------------------------------------------------------
// Toggle for customer.reqs_cascade_to_sites

function CascadeToggle({ current, customerId, disabled, onSaved, onError }) {
  const [busy, setBusy] = useState(false);
  const flip = async () => {
    setBusy(true);
    try {
      const r = await api.setCustomerCascade(customerId, !current);
      onSaved(r.reqs_cascade_to_sites);
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={flip}
      disabled={disabled || busy}
      title={current
        ? 'Customer-level requirements apply to every site unless overridden. Click to disable cascading.'
        : 'Each site stands alone — customer-level requirements do NOT cascade. Click to re-enable.'}
      style={{
        background: current ? '#D1FAE5' : '#FEF3C7',
        color: current ? '#065F46' : '#92400E',
        border: `1px solid ${current ? '#10B981' : '#F59E0B'}`,
        padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        cursor: disabled || busy ? 'not-allowed' : 'pointer',
      }}
    >
      {busy ? '…' : (current ? '✓ Apply to all sites' : '⚠ Per-site only')}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tab button helper

function TabBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? '#1F2937' : 'transparent',
        color: active ? '#fff' : '#374151',
        border: 'none',
        borderBottom: active ? '2px solid #E36F2C' : '2px solid transparent',
        padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
      }}
    >{children}</button>
  );
}

// ---------------------------------------------------------------------------
// Sites section (header + table + add/edit row)

function SitesSection({ customerId, sites, editAllowed, onChange, onError }) {
  const [editing, setEditing] = useState(null); // null | {} (new) | existing row
  const startCreate = () => setEditing({ name: '', address: '', city: '', state: '', zip: '', ship_to_code: '', notes: '', is_active: true });
  const startEdit = (s) => setEditing({ ...s });
  const cancel = () => setEditing(null);
  const save = async () => {
    try {
      if (editing.id) await api.updateCustomerSite(editing.id, editing);
      else await api.createCustomerSite(customerId, editing);
      setEditing(null);
      onChange();
    } catch (e) { onError(e.message); }
  };
  const remove = async (s) => {
    if (!confirm(`Delete site "${s.name}"? Any site-specific requirements + uploaded badging forms will be deleted too.`)) return;
    try { await api.deleteCustomerSite(s.id); onChange(); }
    catch (e) { onError(e.message); }
  };

  return (
    <div style={{ ...styles.card, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ ...styles.h2, margin: 0 }}>
          🏢 Ship-To Sites
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · {sites.length} site{sites.length === 1 ? '' : 's'}
          </span>
        </h3>
        {editAllowed && (
          <button style={styles.btn} onClick={startCreate}>+ Add Site</button>
        )}
      </div>

      {editing && (
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 8px' }}>
            {editing.id ? 'Edit Site' : 'New Site'}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
            <Field label="Site Name *">
              <input style={styles.input} value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
            </Field>
            <Field label="Ship-To Code">
              <input style={styles.input} value={editing.ship_to_code || ''} onChange={e => setEditing({ ...editing, ship_to_code: e.target.value })} />
            </Field>
            <Field label="Address">
              <input style={styles.input} value={editing.address || ''} onChange={e => setEditing({ ...editing, address: e.target.value })} />
            </Field>
            <Field label="City">
              <input style={styles.input} value={editing.city || ''} onChange={e => setEditing({ ...editing, city: e.target.value })} />
            </Field>
            <Field label="State">
              <input style={styles.input} value={editing.state || ''} onChange={e => setEditing({ ...editing, state: e.target.value })} />
            </Field>
            <Field label="Zip">
              <input style={styles.input} value={editing.zip || ''} onChange={e => setEditing({ ...editing, zip: e.target.value })} />
            </Field>
            <Field label="Active">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
                <input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                Active
              </label>
            </Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Notes">
                <textarea style={{ ...styles.input, minHeight: 40 }} value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} />
              </Field>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button style={styles.btnSecondary} onClick={cancel}>Cancel</button>
            <button style={styles.btn} onClick={save}>{editing.id ? 'Save' : 'Create'}</button>
          </div>
        </div>
      )}

      {sites.length === 0 ? (
        <div style={{ padding: 16, color: '#9CA3AF', textAlign: 'center', fontSize: 13 }}>
          No sites yet. Add a site to enable per-location requirement overrides.
        </div>
      ) : (
        <table style={{ ...styles.table, fontSize: 13 }}>
          <thead><tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Ship-To</th>
            <th style={styles.th}>City</th>
            <th style={styles.th}>State</th>
            <th style={styles.th}>Active</th>
            {editAllowed && <th style={styles.th}></th>}
          </tr></thead>
          <tbody>
            {sites.map(s => (
              <tr key={s.id} style={!s.is_active ? { opacity: 0.5 } : {}}>
                <td style={{ ...styles.td, fontWeight: 600 }}>{s.name}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>{s.ship_to_code || '—'}</td>
                <td style={styles.td}>{s.city || '—'}</td>
                <td style={styles.td}>{s.state || '—'}</td>
                <td style={styles.td}>{s.is_active ? '✓' : '—'}</td>
                {editAllowed && (
                  <td style={styles.td}>
                    <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={() => startEdit(s)}>Edit</button>
                    {' '}
                    <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => remove(s)}>Del</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The big requirements editor -- shared between customer-level and per-site.

const blankReqs = () => ({
  disa_required: false,
  disa_tests: [],
  disa_max_age_days: '',
  badge_valid_days: '',
  isn_required: false,
  training_required_certs: [],
  training_required_cert_codes: [],
  safety_council_required: false,
  safety_council_classes: [],
  safety_council_class_codes: [],
  safety_council_facility: '',
  per_diem_min_miles: '',
  per_diem_max_miles: '',
  rate_type: '',
  per_diem_supervision_rate: '',
  per_diem_technician_rate: '',
  rate_notes: '',
});

// Pulled from the DISA report's distinct Policy Names + the supplemental
// test-type categories Ops actually uses. Physical dropped per George's
// 2026-05-18 review.
const DISA_TEST_OPTIONS = [
  // Common test categories
  'Drug Test',
  'Background Check',
  'Alcohol',
  'Random Pool',
  // Consortium / policy enrollments (must match Policy Names in DISA exports)
  'DCCHT Consortium',
  'DCCHT w/Randoms Consortium',
  'NASAP Consortium',
  'FMCSA Compliance',
  'Default Corporate',
];

// Codes run PARALLEL to class names (codes[i] belongs to names[i]). Pad or
// trim the stored codes to the names' length on load so add/remove rows and
// the save always carry aligned pairs; the server keeps a blank code in
// position as '' (utils/certMatch.js alignReqPairs).
const alignCodes = (names, codes) => (names || []).map((_, i) => (codes || [])[i] ?? '');

function RequirementsForm({ scope, customerId, siteId, siteName, existing, baseline, editAllowed, inheritedFrom, headerNote, onSaved, onError, onDeleteOverride }) {
  // Nothing saved yet: start from the SSS baseline (server utils/requirementBaseline.js).
  const seed = existing || inheritedFrom || (baseline ? { ...blankReqs(), ...baseline } : blankReqs());
  const [form, setFormRaw] = useState({
    disa_required: !!seed.disa_required,
    disa_tests: seed.disa_tests || [],
    disa_max_age_days: seed.disa_max_age_days ?? '',
    badge_valid_days: seed.badge_valid_days ?? '',
    training_required_certs: seed.training_required_certs || [],
    training_required_cert_codes: alignCodes(seed.training_required_certs, seed.training_required_cert_codes),
    safety_council_required: !!seed.safety_council_required,
    safety_council_classes: seed.safety_council_classes || [],
    safety_council_class_codes: alignCodes(seed.safety_council_classes, seed.safety_council_class_codes),
    safety_council_facility: seed.safety_council_facility || '',
    per_diem_min_miles: seed.per_diem_min_miles ?? '',
    per_diem_max_miles: seed.per_diem_max_miles ?? '',
    rate_type: seed.rate_type || '',
    per_diem_supervision_rate: seed.per_diem_supervision_rate ?? '',
    per_diem_technician_rate: seed.per_diem_technician_rate ?? '',
    rate_notes: seed.rate_notes || '',
    badging_email: seed.badging_email || '',
    required_badging_docs: seed.required_badging_docs || [],
    isn_required: !!seed.isn_required,
  });
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  // Dirty tracking: every form mutation flips it; saving clears it. Drives
  // the per-card Save buttons and the leave-without-saving warning.
  const [dirty, setDirty] = useState(false);
  const setForm = (v) => { setDirty(true); setFormRaw(v); };
  useEffect(() => {
    if (!dirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Badging form filler. Mapping is an edit-role action; filling is also
  // open to ops (server-enforced) — dispatch generates packets.
  const { user } = useAuth();
  const canFill = editAllowed || user.role === 'ops';
  const [mapFile, setMapFile] = useState(null);
  const [fillOpen, setFillOpen] = useState(false);
  const [runs, setRuns] = useState(null); // null = hidden, [] = loaded
  const toggleRuns = async () => {
    if (runs) return setRuns(null);
    try { setRuns(await api.badgingRuns(customerId)); }
    catch (e) { onError(e.message); }
  };

  // Entry-time warnings on the class rows (utils/reqCodes.js): a course code
  // typed into the name box, and a code no training record carries (a coded
  // row matches certificate_no ONLY, so a wrong code reads red for everyone).
  // Which codes anyone holds comes from GET /customer-requirements/known-codes,
  // debounced; only the newest lookup lands. Advisory, never a block on save.
  const [codeReport, setCodeReport] = useState({});
  const codeQuery = codesToCheck([
    ...form.training_required_certs.map((n, i) => ({ name: n, code: form.training_required_cert_codes[i] })),
    ...form.safety_council_classes.map((n, i) => ({ name: n, code: form.safety_council_class_codes[i] })),
  ]).join(',');
  useEffect(() => {
    if (!codeQuery) { setCodeReport({}); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      api.knownCertCodes(codeQuery.split(','))
        .then(r => { if (alive) setCodeReport((r && r.codes) || {}); })
        .catch(() => { /* a failed lookup shows no code warnings */ });
    }, 500);
    return () => { alive = false; clearTimeout(t); };
  }, [codeQuery]);
  const codeWarnings = (name, code) => rowWarnings({ name, code }, codeReport).map((w, j) => (
    <div key={j} style={{ fontSize: 11, color: '#B45309', margin: '-2px 0 6px' }}>⚠ {w}</div>
  ));

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setArr = (k, idx, v) => {
    const next = [...form[k]];
    next[idx] = v;
    setForm({ ...form, [k]: next });
  };
  const addToArr = (k, v = '') => setForm({ ...form, [k]: [...form[k], v] });
  const removeFromArr = (k, idx) => {
    const next = [...form[k]];
    next.splice(idx, 1);
    setForm({ ...form, [k]: next });
  };
  // Parallel arrays for Safety Council classes + codes (both shrink/grow together)
  const removeScRow = (idx) => {
    const cls = [...form.safety_council_classes]; cls.splice(idx, 1);
    const cod = [...form.safety_council_class_codes]; cod.splice(idx, 1);
    setForm({ ...form, safety_council_classes: cls, safety_council_class_codes: cod });
  };
  const addScRow = () => setForm({
    ...form,
    safety_council_classes: [...form.safety_council_classes, ''],
    safety_council_class_codes: [...form.safety_council_class_codes, ''],
  });
  // Same parallel-array pattern for the Training card's required certs +
  // optional codes (2026-07-14 — covers ISN-tracked or any code-identified
  // course without a dedicated ISN card).
  const removeTrRow = (idx) => {
    const cls = [...form.training_required_certs]; cls.splice(idx, 1);
    const cod = [...form.training_required_cert_codes]; cod.splice(idx, 1);
    setForm({ ...form, training_required_certs: cls, training_required_cert_codes: cod });
  };
  const addTrRow = () => setForm({
    ...form,
    training_required_certs: [...form.training_required_certs, ''],
    training_required_cert_codes: [...form.training_required_cert_codes, ''],
  });

  const save = async () => {
    setBusy(true);
    try {
      if (scope === 'customer') await api.saveCustomerLevelRequirements(customerId, form);
      else                      await api.saveSiteLevelRequirements(siteId, form);
      onSaved();
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };

  // One Save per card — every card saves the SAME requirements record (all
  // cards at once), the duplication is purely so a save button is always in
  // view. Turns orange when there are unsaved changes.
  const cardSave = editAllowed ? (
    <button
      style={{
        ...(dirty ? styles.btn : styles.btnSecondary), fontSize: 11, padding: '4px 12px',
        ...(savedFlash ? { background: '#10B981', color: '#fff', border: 'none' } : {}),
      }}
      onClick={save} disabled={busy}
      title="Saves ALL requirement cards on this tab, not just this one"
    >
      {busy ? 'Saving…' : savedFlash ? '✓ Saved' : dirty ? '● Save' : 'Save'}
    </button>
  ) : null;

  // Mirrors the server's extension whitelist so one stray file (an Outlook
  // drag often includes inline images like .wmz) skips with a notice instead
  // of failing the whole drop.
  const BADGING_ALLOWED_EXT = ['.pdf','.doc','.docx','.xls','.xlsx','.csv','.jpg','.jpeg','.png','.gif','.txt','.zip'];
  const uploadFiles = async (files) => {
    const all = Array.from(files || []).filter(Boolean);
    if (!all.length || uploadBusy) return;
    const ok = all.filter(f => BADGING_ALLOWED_EXT.includes(('.' + f.name.split('.').pop()).toLowerCase()));
    const skipped = all.filter(f => !ok.includes(f));
    if (!ok.length) { alert(`No uploadable files — allowed types: ${BADGING_ALLOWED_EXT.join(', ')}`); return; }
    setUploadBusy(true);
    try {
      await api.uploadBadgingFiles(scope, scope === 'customer' ? customerId : siteId, ok);
      if (skipped.length) alert(`Uploaded ${ok.length} file${ok.length === 1 ? '' : 's'}. Skipped (type not allowed): ${skipped.map(f => f.name).join(', ')}`);
      onSaved();
    } catch (e) { onError(e.message); }
    finally { setUploadBusy(false); }
  };

  const removeFile = async (f) => {
    if (!confirm(`Remove "${f.filename}"?`)) return;
    try { await api.deleteBadgingFile(f.id); onSaved(); }
    catch (e) { onError(e.message); }
  };

  return (
    <>
      {headerNote && (
        <div style={{
          background: scope === 'site' && inheritedFrom ? '#F0F9FF' : '#FEF3C7',
          border: `1px solid ${scope === 'site' && inheritedFrom ? '#BAE6FD' : '#F59E0B'}`,
          color: scope === 'site' && inheritedFrom ? '#075985' : '#78350F',
          padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 12,
        }}>
          {headerNote}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
        {/* DISA */}
        <Section title="🛡 DISA / Background" action={cardSave}>
          <CheckboxField label="DISA required" value={form.disa_required} onChange={v => set('disa_required', v)} disabled={!editAllowed} />
          <DropdownPicker
            label="Tests / policies required"
            options={DISA_TEST_OPTIONS}
            selected={form.disa_tests}
            onChange={v => set('disa_tests', v)}
            disabled={!editAllowed || !form.disa_required}
            placeholder="— Add a test / policy —"
          />
          <Field label="Max age (days) of last test">
            <input type="number" min="0" style={styles.input} disabled={!editAllowed || !form.disa_required}
              value={form.disa_max_age_days} onChange={e => set('disa_max_age_days', e.target.value)}
              placeholder="e.g. 90" />
          </Field>
          <Field label="Badge valid (days) once issued">
            <input type="number" min="0" style={styles.input} disabled={!editAllowed}
              value={form.badge_valid_days} onChange={e => set('badge_valid_days', e.target.value)}
              placeholder="365" />
          </Field>
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: -6, marginBottom: 8 }}>
            A permanent site badge, once issued (Roster → 🪪 Badges), is valid access for this many days
            regardless of drug-test age — the test only has to be fresh to get the badge initially.
          </div>
          <div style={{ marginTop: 10, borderTop: '1px solid #F3F4F6', paddingTop: 10 }}>
            <CheckboxField label="ISN required (employee must have an ISN number)" value={form.isn_required} onChange={v => set('isn_required', v)} disabled={!editAllowed} />
          </div>
        </Section>

        {/* Training */}
        <Section title="🎓 Training" action={cardSave}>
          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
            Any required class — Safety Council, ISNetworld-tracked, or otherwise. Code is optional;
            add it when the source assigns one (e.g. an ISN course ID) so it matches by CODE first
            instead of relying on name text. Separate equivalent alternates with "/" in either field
            (e.g. an initial class and its refresher) — an employee needs only ONE of the alternates
            on file to satisfy the row. Log completions under Operations → Training on the employee —
            tag the Issuer field (e.g. "ISN") for traceability; matching works regardless of issuer.
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Required certifications + codes</div>
          {form.training_required_certs.length === 0 && (
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6 }}>No certifications added yet.</div>
          )}
          {form.training_required_certs.map((cert, i) => (
            <div key={i}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <input style={{ ...styles.input, flex: 2 }} disabled={!editAllowed}
                  placeholder="e.g. OSHA 10, H2S Awareness, ISN Fall Protection" value={cert}
                  onChange={e => setArr('training_required_certs', i, e.target.value)} />
                <input style={{ ...styles.input, flex: 1 }} disabled={!editAllowed}
                  placeholder="Code (optional)" value={form.training_required_cert_codes[i] || ''}
                  onChange={e => setArr('training_required_cert_codes', i, e.target.value)} />
                {editAllowed && (
                  <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => removeTrRow(i)}>×</button>
                )}
              </div>
              {codeWarnings(cert, form.training_required_cert_codes[i])}
            </div>
          ))}
          {editAllowed && (
            <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={addTrRow}>
              + Add Certification
            </button>
          )}
        </Section>

        {/* Safety Council */}
        <Section title="🏛 Safety Council" action={cardSave}>
          <CheckboxField label="Safety Council required" value={form.safety_council_required} onChange={v => set('safety_council_required', v)} disabled={!editAllowed} />
          <Field label="Facility (where tests must be taken)">
            <input style={styles.input} disabled={!editAllowed || !form.safety_council_required}
              value={form.safety_council_facility} onChange={e => set('safety_council_facility', e.target.value)}
              placeholder="e.g. Beaumont Industrial Safety Council" />
          </Field>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Classes + codes</div>
            {/* Decision 6 (2026-09-23): the checkbox does not gate this list.
                Every class listed here is checked for Site Ready either way,
                so the list stays editable with the box unticked. */}
            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6 }}>
              Every class listed here is checked for Site Ready whether or not "Safety Council required" is ticked.
              Remove a class to stop checking it.
            </div>
            {form.safety_council_classes.length === 0 && (
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6 }}>No classes added yet.</div>
            )}
            {form.safety_council_classes.map((cls, i) => (
              <div key={i}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                  <input style={{ ...styles.input, flex: 2 }} disabled={!editAllowed}
                    placeholder="Class name" value={cls} onChange={e => setArr('safety_council_classes', i, e.target.value)} />
                  <input style={{ ...styles.input, flex: 1 }} disabled={!editAllowed}
                    placeholder="Code" value={form.safety_council_class_codes[i] || ''} onChange={e => setArr('safety_council_class_codes', i, e.target.value)} />
                  {editAllowed && (
                    <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => removeScRow(i)}>×</button>
                  )}
                </div>
                {codeWarnings(cls, form.safety_council_class_codes[i])}
              </div>
            ))}
            {editAllowed && (
              <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={addScRow}>
                + Add Class
              </button>
            )}
          </div>
        </Section>

        {/* Per Diem */}
        <Section title="🚗 Per Diem Qualification" action={cardSave}>
          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
            Distance (miles) from employee's home base to this site.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Field label="Min miles">
              <input type="number" min="0" style={styles.input} disabled={!editAllowed}
                value={form.per_diem_min_miles} onChange={e => set('per_diem_min_miles', e.target.value)} placeholder="e.g. 50" />
            </Field>
            <Field label="Max miles">
              <input type="number" min="0" style={styles.input} disabled={!editAllowed}
                value={form.per_diem_max_miles} onChange={e => set('per_diem_max_miles', e.target.value)} placeholder="e.g. 500" />
            </Field>
          </div>
        </Section>

        {/* Per Diem Bill Rate -- billed separately for supervision vs technician
            headcount. Rate type (MSA vs Project) applies to both. */}
        <Section title="💰 Per Diem Bill Rate" action={cardSave}>
          <Field label="Rate type">
            <select style={styles.input} disabled={!editAllowed}
              value={form.rate_type} onChange={e => set('rate_type', e.target.value)}>
              <option value="">— Not set —</option>
              <option value="MSA">MSA (Master Service Agreement)</option>
              <option value="Project">Project (per-job, may change)</option>
            </select>
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Field label="Supervision ($)">
              <input type="number" min="0" step="0.01" style={styles.input} disabled={!editAllowed}
                value={form.per_diem_supervision_rate}
                onChange={e => set('per_diem_supervision_rate', e.target.value)}
                placeholder="0.00" />
            </Field>
            <Field label="Technician ($)">
              <input type="number" min="0" step="0.01" style={styles.input} disabled={!editAllowed}
                value={form.per_diem_technician_rate}
                onChange={e => set('per_diem_technician_rate', e.target.value)}
                placeholder="0.00" />
            </Field>
          </div>
          <Field label="Rate notes">
            <textarea style={{ ...styles.input, minHeight: 48 }} disabled={!editAllowed}
              value={form.rate_notes} onChange={e => set('rate_notes', e.target.value)}
              placeholder="Effective date, contract reference, escalation terms..." />
          </Field>
        </Section>

        {/* Badging documents (multi-file) */}
        <Section title="📄 Badging Documents" action={cardSave}>
          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
            The customer's badging packet: badge application, escorted badge request, release forms, safety rules, vehicle sticker forms. Employees fill out copies when getting badged for the site.
          </div>
          {(existing?.badging_files?.length > 0) ? (
            <div style={{ marginBottom: 8 }}>
              {existing.badging_files.map(f => (
                <div key={f.id} style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', padding: '8px 10px', borderRadius: 6, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                    title={`Download ${f.filename}`}
                    onClick={() => api.downloadBadgingFile(f.id, f.filename).catch(e => onError(e.message))}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#0369A1' }}>
                      📎 {f.filename}
                      {f.mapping_status === 'Confirmed' && (
                        <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#065F46', background: '#D1FAE5', padding: '1px 6px', borderRadius: 8 }} title="Field mapping confirmed — this form can be auto-filled">FILLABLE</span>
                      )}
                      {f.mapping_status === 'Draft' && (
                        <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '1px 6px', borderRadius: 8 }} title="Field mapping drafted — confirm it to enable filling">MAPPING DRAFT</span>
                      )}
                      {(!/\.pdf$/i.test(f.filename) || f.fillable_fields === 0) && (
                        <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#6B7280', background: '#F3F4F6', padding: '1px 6px', borderRadius: 8 }} title="No fillable form fields — view/download only">REFERENCE ONLY</span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>
                      {f.uploaded_at ? fmtDateTime(f.uploaded_at) : '—'}
                      {f.size_bytes ? ` · ${f.size_bytes >= 1048576 ? (f.size_bytes / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(f.size_bytes / 1024)) + ' KB'}` : ''}
                      {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''}
                    </div>
                  </div>
                  <button
                    style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap' }}
                    title={`Download ${f.filename}`}
                    onClick={() => api.downloadBadgingFile(f.id, f.filename).catch(e => onError(e.message))}
                  >⬇ Download</button>
                  {editAllowed && /\.pdf$/i.test(f.filename) && f.fillable_fields !== 0 && (
                    <button
                      style={{
                        ...(f.mapping_status === 'Confirmed' ? styles.btn : styles.btnSecondary),
                        fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap',
                        ...(f.mapping_status === 'Draft' ? { background: '#FEF3C7', borderColor: '#F59E0B', color: '#92400E' } : {}),
                      }}
                      title={f.mapping_status === 'Confirmed' ? 'Mapped and fillable — click to review or edit the mapping'
                        : f.mapping_status === 'Draft' ? 'Mapping drafted but NOT confirmed — finish it to enable filling'
                        : 'Not mapped yet — map this form\'s fields so it can be auto-filled'}
                      onClick={() => setMapFile(f)}
                    >{f.mapping_status === 'Confirmed' ? '✓ Mapped' : f.mapping_status === 'Draft' ? '🪄 Draft…' : '🪄 Map'}</button>
                  )}
                  {editAllowed && (
                    <button
                      style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap' }}
                      title={`Delete ${f.filename} — removes it for everyone`}
                      onClick={() => removeFile(f)}
                    >🗑 Delete</button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>No badging documents uploaded yet.</div>
          )}
          {editAllowed && (
            <label
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
              style={{
                display: 'block', textAlign: 'center', cursor: 'pointer',
                padding: '16px 10px', borderRadius: 6,
                border: `2px dashed ${dragOver ? '#E36F2C' : '#D1D5DB'}`,
                background: dragOver ? '#FFF7ED' : '#FAFAFA',
                fontSize: 12, color: dragOver ? '#E36F2C' : '#6B7280',
              }}
            >
              {uploadBusy ? 'Uploading…' : '⬆ Drag & drop files here, or click to browse (multiple OK)'}
              <input type="file" multiple style={{ display: 'none' }} disabled={uploadBusy}
                onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} />
            </label>
          )}
          <div style={{ marginTop: 10 }}>
            <Field label="Badging submission email (where the customer wants packets sent)">
              <input style={styles.input} disabled={!editAllowed}
                value={form.badging_email} onChange={e => set('badging_email', e.target.value)}
                placeholder="e.g. ftpcbadgeid@ftpc.fpcusa.com" />
            </Field>
          </div>
          <div style={{ marginTop: 10 }}>
            <DropdownPicker
              label="Documents required with every submission (per employee)"
              options={DOC_TYPES}
              selected={form.required_badging_docs}
              onChange={v => set('required_badging_docs', v)}
              disabled={!editAllowed}
              placeholder="— Add a required document —"
            />
            <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
              Same list as the upload labels on employee documents, so matching is automatic. The wizard warns when a chosen employee is missing one and pre-checks these for the email. Remember to Save.
            </div>
            {/* Decision 5 (2026-09-23): packet documents are a checklist. */}
            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>
              Only TWIC changes Site Ready. The rest is the badging packet checklist: Site Access and the compliance
              modal show each one as on file or not, and never turn anyone red over it.
            </div>
          </div>
          {canFill && (existing?.badging_files || []).some(f => f.mapping_status === 'Confirmed') && (
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button style={{ ...styles.btn, fontSize: 12 }} onClick={() => setFillOpen(true)}>
                🖨 Fill Forms for Employees
              </button>
              <button style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={toggleRuns}>
                {runs ? 'Hide packets' : '📜 Filled packets'}
              </button>
            </div>
          )}
          {runs && (
            <div style={{ marginTop: 8 }}>
              {runs.length === 0 && <div style={{ fontSize: 11, color: '#9CA3AF' }}>No packets generated yet.</div>}
              {runs.map(r => (
                <div key={r.id} style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: 8, marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>
                        {fmtDateTime(r.created_at)} · {(r.employee_names || []).join(', ')}
                      </div>
                      <div style={{ fontSize: 10, color: '#6B7280' }}>
                        {r.created_by_name || ''}{r.emailed_to ? ` · emailed to ${r.emailed_to}` : ''}
                      </div>
                    </div>
                    {editAllowed && (
                      <button
                        style={{ ...styles.btnDanger, fontSize: 10, padding: '3px 8px' }}
                        title="Delete this packet and its generated files"
                        onClick={async () => {
                          if (!confirm(`Delete this packet (${(r.employee_names || []).join(', ')})? The generated files are removed too.`)) return;
                          try { await api.deleteBadgingRun(r.id); setRuns(prev => prev.filter(x => x.id !== r.id)); }
                          catch (e) { onError(e.message); }
                        }}
                      >🗑 Delete</button>
                    )}
                  </div>
                  {(r.files || []).map(f => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginTop: 4 }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {f.filename}</span>
                      <button style={{ ...styles.btnSecondary, fontSize: 10, padding: '2px 8px' }}
                        onClick={() => api.downloadBadgingRunFile(f.id, f.filename).catch(e => onError(e.message))}>⬇</button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {editAllowed && (
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {scope === 'site' && existing && (
            <button style={{ ...styles.btnDanger }} onClick={onDeleteOverride}>
              🗑 Remove Site Override
            </button>
          )}
          <button
            style={{ ...styles.btn, ...(savedFlash ? { background: '#10B981' } : {}) }}
            onClick={save} disabled={busy}
          >
            {busy ? 'Saving…' : savedFlash ? '✓ Saved' : (existing ? 'Save Changes' : 'Create Requirements')}
          </button>
        </div>
      )}

      {mapFile && (
        <MappingModal
          file={mapFile}
          onClose={() => setMapFile(null)}
          onSaved={onSaved}
          onError={onError}
        />
      )}
      {fillOpen && (
        <FillWizard
          customerId={customerId}
          defaultEmail={form.badging_email || existing?.badging_email || ''}
          requiredDocs={form.required_badging_docs?.length ? form.required_badging_docs : (existing?.required_badging_docs || [])}
          files={existing?.badging_files || []}
          onClose={() => { setFillOpen(false); if (runs) api.badgingRuns(customerId).then(setRuns).catch(() => {}); }}
          onError={onError}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Small helpers

const Field = ({ label, children }) => (
  <div>
    <label style={styles.label}>{label}</label>
    {children}
  </div>
);

const Section = ({ title, action, children }) => (
  <div style={{ ...styles.card, padding: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 10px 0' }}>
      <h4 style={{ fontSize: 13, fontWeight: 700, margin: 0, color: '#1F2937' }}>{title}</h4>
      {action || null}
    </div>
    {children}
  </div>
);

const CheckboxField = ({ label, value, onChange, disabled }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}>
    <input type="checkbox" checked={value} disabled={disabled} onChange={e => onChange(e.target.checked)} />
    {label}
  </label>
);

// Dropdown-based multi-select. Shows the picked options as removable
// chips on top, and a <select> below to add a new one. Picking from the
// select appends to the array and resets the select. Used for DISA where
// the option list is long enough that chip-buttons get unwieldy.
const DropdownPicker = ({ label, options, selected, onChange, disabled, placeholder }) => {
  const remaining = options.filter(o => !selected.includes(o));
  const add = (v) => {
    if (!v || selected.includes(v)) return;
    onChange([...selected, v]);
  };
  const remove = (v) => onChange(selected.filter(s => s !== v));
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {selected.map(s => (
            <span key={s} style={{
              background: '#D1FAE5', color: '#065F46',
              border: '1px solid #10B981',
              padding: '3px 6px 3px 10px', borderRadius: 12,
              fontSize: 11, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {s}
              {!disabled && (
                <button type="button" onClick={() => remove(s)} title="Remove"
                  style={{ background: 'transparent', border: 'none', color: '#065F46', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
              )}
            </span>
          ))}
        </div>
      )}
      {selected.length === 0 && (
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6 }}>None selected.</div>
      )}
      {!disabled && remaining.length > 0 && (
        <select
          style={styles.input}
          value=""
          onChange={e => { add(e.target.value); e.target.value = ''; }}
        >
          <option value="">{placeholder || '— Add —'}</option>
          {remaining.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {!disabled && remaining.length === 0 && selected.length > 0 && (
        <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>All options selected.</div>
      )}
    </div>
  );
};

const MultiCheckbox = ({ label, options, selected, onChange, disabled }) => {
  const toggle = (opt) => {
    if (disabled) return;
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else                        onChange([...selected, opt]);
  };
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {options.map(opt => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt} type="button" onClick={() => toggle(opt)} disabled={disabled}
              style={{
                background: on ? '#D1FAE5' : '#F3F4F6',
                color: on ? '#065F46' : '#6B7280',
                border: `1px solid ${on ? '#10B981' : '#D1D5DB'}`,
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >{on ? '✓ ' : ''}{opt}</button>
          );
        })}
      </div>
    </div>
  );
};

